import { sdk } from '@/lib/sdk'

/**
 * Waits for hosted images that the server is still processing (`queued` → `processing` →
 * `ready` | `failed`). Every watcher shares one poll: one `POST /media/urls` per tick for all ids,
 * starting at 3 s and backing off to 15 s while nothing changes, so a gallery of uploads costs a
 * handful of requests and stays inside the read budget.
 */

export type ProcessingOutcome = 'ready' | 'failed'
type Listener = (id: string, outcome: ProcessingOutcome) => void

const FIRST_DELAY = 3_000
const MAX_DELAY = 15_000
const BATCH = 100

const watched = new Map<string, Set<Listener>>()
let timer: ReturnType<typeof setTimeout> | undefined
let delay = FIRST_DELAY
let polling = false

const schedule = () => {
  if (timer || polling || watched.size === 0) return
  timer = setTimeout(poll, delay)
}

const settle = (id: string, outcome: ProcessingOutcome) => {
  const listeners = watched.get(id)
  watched.delete(id)
  listeners?.forEach((listener) => listener(id, outcome))
}

const poll = async () => {
  timer = undefined
  polling = true
  let changed = false
  const ids = [...watched.keys()]
  try {
    for (let i = 0; i < ids.length; i += BATCH) {
      const entries = await sdk.media.getUrls(ids.slice(i, i + BATCH))
      for (const entry of entries) {
        if (entry.processingStatus === 'failed') { settle(entry.id, 'failed'); changed = true }
        else if (entry.url || entry.processingStatus === 'ready') { settle(entry.id, 'ready'); changed = true }
        // Not ours to watch any more (deleted, no access): stop asking.
        else if (entry.error) { watched.delete(entry.id); changed = true }
      }
    }
  } catch {
    // A failed poll is retried on the next tick, with the backoff.
  } finally {
    polling = false
    delay = changed ? FIRST_DELAY : Math.min(Math.round(delay * 1.5), MAX_DELAY)
    schedule()
  }
}

/** Calls `listener` once per id when it is ready or failed. Returns the unsubscribe. */
export const watchMediaProcessing = (ids: string[], listener: Listener): (() => void) => {
  const fresh = ids.filter((id) => !!id)
  if (fresh.length === 0) return () => undefined
  for (const id of fresh) {
    if (!watched.has(id)) watched.set(id, new Set())
    watched.get(id)!.add(listener)
  }
  delay = FIRST_DELAY
  if (timer) { clearTimeout(timer); timer = undefined }
  schedule()
  return () => {
    for (const id of fresh) {
      const listeners = watched.get(id)
      listeners?.delete(listener)
      if (listeners && listeners.size === 0) watched.delete(id)
    }
  }
}
